/**
 * TirvuClock — relógio MONOTÔNICO do aparelho (tempo desde o boot), versão web.
 *
 * Mesma API de `modules/tirvu-clock/index.ts` do app nativo: `disponivel`, `uptimeMs()`
 * (síncrono), `bootCount()`.
 *
 * WEB: o browser NÃO expõe um contador desde o boot que conte o deep sleep. Então, como o
 * próprio módulo nativo prevê para "build antigo em campo, web", toda a API devolve `null` e
 * o `RelogioConfiavel` roda em MODO DEGRADADO (`fonte_hora = "D"`, `idade_ms = ""`) — nada
 * quebra, só perde a proteção (docs/port/01 §4.2, 03 §9.5).
 *
 * Por que NÃO usamos `performance.timeOrigin + performance.now()` como uptime:
 *  1. `performance.now()` é a "GÊMEA ERRADA" que o comentário do nativo proíbe: no Chrome
 *     Android ele usa CLOCK_MONOTONIC, que CONGELA durante o deep sleep. Celular no bolso
 *     com a tela apagada passa quase todo o tempo em sleep — uma batida offline de 3h30
 *     atrás seria contada como "20 minutos" e o servidor reconstruiria a hora ERRADA
 *     (`data_log = agoraServidor - idade`), com `fonte: 'M'` (confiável).
 *  2. Ele recomeça a cada carregamento de página. `idadeMs(ponto.uptimeMs)` de uma batida
 *     gravada em OUTRA carga daria uma idade errada (não `null`). Somar `timeOrigin` só troca
 *     o problema: `timeOrigin` é o relógio de PAREDE no início da carga — justamente o
 *     relógio que o usuário pode adiantar/atrasar.
 *  Devolver `null` ("não sei") é o que o `RelogioConfiavel` sabe tratar com segurança.
 *
 * `uptimeDaPaginaMs()` existe só para diagnóstico/log (tempo desde o carregamento da página);
 * NÃO usar no relógio confiável.
 */

/** false = sem relógio monotônico de boot → o relógio confiável roda degradado. */
export const disponivel = false;

/**
 * ms desde o boot do aparelho, ou `null` (sempre `null` no web — ver cabeçalho).
 * Síncrono, como no nativo.
 */
export const uptimeMs = (): number | null => null;

/**
 * Identificador da SESSÃO DE BOOT, ou `null` quando não dá para saber (sempre no web).
 * `null` significa "não sei", nunca "mesmo boot" — âncora lida do disco é descartada.
 */
export const bootCount = (): number | null => null;

/**
 * Diagnóstico: ms desde o início desta carga de página (`performance.now()`), ou `null`.
 * ⚠ Congela no sleep em alguns SOs e zera a cada recarga — nunca usar como uptime.
 */
export const uptimeDaPaginaMs = (): number | null => {
  try {
    if (typeof performance === "undefined") return null;
    const v = performance.now();
    return Number.isFinite(v) && v > 0 ? v : null;
  } catch {
    return null;
  }
};

const TirvuClock = { disponivel, uptimeMs, bootCount, uptimeDaPaginaMs };

export default TirvuClock;
